// src/components/operationCenter/widgets/ZScoreWidget.tsx
// Z-Score oscillator with ±2σ (entry) and ±3σ (extreme) threshold bands.
// Displays signal_value from the signal series which is the normalised z-score.

import React, { useMemo } from "react";
import { Activity } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { WidgetProps } from "@/types/dashboard";
import type { SignalChartConfig } from "@/types/signals";
import { useSignalSeries } from "@/hooks/signals/useSignalSeries";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import {
  createGrid,
  createXAxis,
  createTooltip,
  CHART_COLORS,
} from "@/utils/charts/theme";
import { formatDateLabel } from "@/utils/charts/dataUtils";

function formatTooltipDate(raw: string): string {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} — ${hh}:${mm}`;
}

const ZScoreWidget: React.FC<WidgetProps> = ({ widget }) => {
  const config = widget.config as SignalChartConfig;
  const signalName = "z_score";

  const { data, isLoading, error } = useSignalSeries({
    signalName,
    ...(config.run_id != null ? { runId: config.run_id } : {}),
  });

  const { dates, values, currentValue } = useMemo(() => {
    if (!data?.data || data.data.length === 0) {
      return {
        dates: [] as string[],
        values: [] as (number | null)[],
        currentValue: null,
      };
    }
    const points = data.data.slice(-100);
    return {
      dates: points.map((p) => p.ts),
      values: points.map((p) => p.signal_value),
      currentValue: points[points.length - 1]?.signal_value ?? null,
    };
  }, [data]);

  // Determine zone label for current value
  const zoneLabel = useMemo(() => {
    if (currentValue == null) return null;
    const abs = Math.abs(currentValue);
    if (abs >= 3) return { text: "EXTREME", color: "text-red-400" };
    if (abs >= 2) return { text: "ENTRY", color: "text-amber-400" };
    return { text: "NORMAL", color: "text-slate-400" };
  }, [currentValue]);

  const chartOption = useMemo((): EChartsOption => {
    if (dates.length === 0) return {};

    // Ensure y-axis covers at least ±3.5 so bands are always visible
    const validValues = values.filter((v): v is number => v != null);
    const dataMin = validValues.length > 0 ? Math.min(...validValues) : -3;
    const dataMax = validValues.length > 0 ? Math.max(...validValues) : 3;
    const yMin = Math.floor(Math.min(dataMin, -3.5) - 0.5);
    const yMax = Math.ceil(Math.max(dataMax, 3.5) + 0.5);

    return {
      backgroundColor: "transparent",
      tooltip: createTooltip({
        trigger: "axis",
        formatter: (params: unknown) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          const p = params[0] as { axisValue: string; value: number | null };
          const val = p.value;
          if (val == null) return "";
          const abs = Math.abs(val);
          let zone = "Normal";
          let color = "#94a3b8";
          if (abs >= 3) {
            zone = "Extreme";
            color = "#ef4444";
          } else if (abs >= 2) {
            zone = "Entry Zone";
            color = "#f59e0b";
          }
          return `<div style="font-weight:500;margin-bottom:4px;color:#9ca3af;">${formatTooltipDate(p.axisValue)}</div>
            <div style="font-weight:600;color:${val >= 0 ? "#22c55e" : "#ef4444"};">Z: ${val.toFixed(4)}</div>
            <div style="font-size:10px;color:${color};margin-top:2px;">${zone}</div>`;
        },
      }),
      grid: createGrid({
        top: 8,
        right: 40,
        bottom: 50,
        left: 12,
        containLabel: true,
      }),
      dataZoom: [
        { type: "inside" },
        {
          type: "slider",
          show: true,
          bottom: 8,
          height: 18,
          borderColor: "#374151",
          textStyle: { color: "#9ca3af", fontSize: 9 },
          brushSelect: false,
          labelFormatter: (_value: number, valueStr: string) =>
            formatTooltipDate(valueStr),
        },
      ],
      xAxis: {
        ...createXAxis({ data: dates, boundaryGap: false }),
        axisLabel: {
          color: CHART_COLORS.axis.label,
          margin: 14,
          showMaxLabel: true,
          formatter: (val: string) => formatDateLabel(val, "short"),
        },
      },
      yAxis: {
        type: "value",
        min: yMin,
        max: yMax,
        axisLabel: {
          color: CHART_COLORS.axis.label,
          formatter: (v: number) => v.toFixed(1),
        },
        splitLine: { show: false },
        axisLine: { show: false },
      },
      series: [
        {
          type: "line",
          data: values,
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 1.5, color: CHART_COLORS.series[0] },
          itemStyle: { color: CHART_COLORS.series[0] },
          // Sigma threshold lines
          markLine: {
            silent: true,
            symbol: ["none", "none"],
            animation: false,
            data: [
              {
                yAxis: 0,
                lineStyle: {
                  color: "#94a3b8",
                  width: 1,
                  type: "solid" as const,
                },
                label: {
                  show: true,
                  formatter: "0",
                  position: "insideEndTop" as const,
                  fontSize: 9,
                  color: "#94a3b8",
                },
              },
              {
                yAxis: 2,
                lineStyle: {
                  color: "#f59e0b",
                  width: 1,
                  type: "dashed" as const,
                },
                label: {
                  show: true,
                  formatter: "+2\u03C3",
                  position: "insideEndTop" as const,
                  fontSize: 9,
                  color: "#f59e0b",
                },
              },
              {
                yAxis: -2,
                lineStyle: {
                  color: "#f59e0b",
                  width: 1,
                  type: "dashed" as const,
                },
                label: {
                  show: true,
                  formatter: "\u22122\u03C3",
                  position: "insideEndBottom" as const,
                  fontSize: 9,
                  color: "#f59e0b",
                },
              },
              {
                yAxis: 3,
                lineStyle: {
                  color: "#ef4444",
                  width: 1,
                  type: "dashed" as const,
                },
                label: {
                  show: true,
                  formatter: "+3\u03C3",
                  position: "insideEndTop" as const,
                  fontSize: 9,
                  color: "#ef4444",
                },
              },
              {
                yAxis: -3,
                lineStyle: {
                  color: "#ef4444",
                  width: 1,
                  type: "dashed" as const,
                },
                label: {
                  show: true,
                  formatter: "\u22123\u03C3",
                  position: "insideEndBottom" as const,
                  fontSize: 9,
                  color: "#ef4444",
                },
              },
            ],
          },
          // Coloured zone areas
          markArea: {
            silent: true,
            data: [
              // Entry zone: ±2σ → ±3σ (amber, subtle)
              [
                { yAxis: 2, itemStyle: { color: "rgba(245,158,11,0.06)" } },
                { yAxis: 3 },
              ],
              [
                { yAxis: -3, itemStyle: { color: "rgba(245,158,11,0.06)" } },
                { yAxis: -2 },
              ],
              // Extreme zone: beyond ±3σ (red, subtle)
              [
                { yAxis: 3, itemStyle: { color: "rgba(239,68,68,0.06)" } },
                { yAxis: yMax },
              ],
              [
                { yAxis: yMin, itemStyle: { color: "rgba(239,68,68,0.06)" } },
                { yAxis: -3 },
              ],
            ],
          },
        },
      ],
    } as EChartsOption;
  }, [dates, values]);

  const formattedValue = currentValue != null ? currentValue.toFixed(4) : "—";
  const valueColor =
    currentValue != null && currentValue >= 0
      ? "text-emerald-400"
      : "text-red-400";

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (error || dates.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-slate-500 p-4">
        <Activity className="w-8 h-8 mb-2 opacity-50" />
        <div className="text-sm">No Z-Score data available</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50 flex-shrink-0 bg-slate-800/30">
        <div className="flex items-center gap-2 min-w-0">
          <Activity className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="text-xs uppercase tracking-wide text-slate-300">
            Z-Score
          </span>
          <span className={`text-xs font-semibold tabular-nums ${valueColor}`}>
            {formattedValue}
          </span>
        </div>
        {zoneLabel && (
          <span
            className={`text-[10px] font-bold tracking-wider ${zoneLabel.color}`}
          >
            {zoneLabel.text}
          </span>
        )}
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-0">
        <EChartsRenderer
          chartOption={chartOption}
          height="100%"
          hasData={dates.length > 0}
          containerClassName=""
        />
      </div>
    </div>
  );
};

export default ZScoreWidget;
